import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import styles from "styles/component/CustomCoursePopOver.module.scss";
import { attributeFieldDefs } from "json/attributeFieldDefs";
import {
  getEffectiveDisplayLabel,
  getEffectiveOptions,
} from "services/attributeFields.utils";

// Data-driven attribute popover. The caller passes the element key plus a
// {description, type, size} values map and an onChange that takes (slot, value).
// The engine handles progressive reveal (each field appears only after the prior
// is answered or auto-hidden), composite-key dependency lookup, and dynamic
// section labels; cascade-clear of invalid child selections happens in the
// caller so all Redux side-effects live in one place.
export const DynamicAttributePopover = ({
  elementKey,
  values,
  onChange,
  errorMsg,
  errorComponent,
  handleSave,
}) => {
  const def = attributeFieldDefs[elementKey];
  if (!def) return null;

  // Collect the visible fields and their effective options/labels in one pass
  // so the render stays declarative.
  const visible = [];
  let priorAnswered = true;
  for (const field of def.fields) {
    const effectiveOptions = getEffectiveOptions(field, def.fields, values);
    if (effectiveOptions.length === 0) continue; // auto-hide
    if (!priorAnswered) break; // progressive reveal
    visible.push({
      field,
      effectiveOptions,
      displayLabel: getEffectiveDisplayLabel(field, def.fields, values),
    });
    if (values[field.slot] === undefined || values[field.slot] === null) {
      priorAnswered = false;
    }
  }

  const handleRadioChange = (slot) => (_event, rawValue) => {
    if (rawValue === "" || rawValue == null) {
      onChange(slot, undefined);
      return;
    }
    const next = Number(rawValue);
    onChange(slot, Number.isNaN(next) ? undefined : next);
  };

  return (
    <Box className={styles.popover_body_new}>
      <FormControl>
        {visible.map(({ field, effectiveOptions, displayLabel }) => {
          const currentValue = values[field.slot];
          const rows = field.optionRows || [effectiveOptions.map((o) => o.label)];

          // One RadioGroup per field — shared name/value/onChange via MUI's
          // context. Rows are pure visual grouping via nested Boxes.
          return (
            <React.Fragment key={field.slot}>
              <FormLabel>{displayLabel}</FormLabel>
              <RadioGroup
                className={styles.marker_option}
                name={`radio-${elementKey}-${field.slot}`}
                value={
                  currentValue === undefined || currentValue === null
                    ? ""
                    : String(currentValue)
                }
                onChange={handleRadioChange(field.slot)}
              >
                {rows.map((row, rowIdx) => {
                  const rowOptions = row
                    .map((label) => effectiveOptions.find((o) => o.label === label))
                    .filter(Boolean);
                  if (!rowOptions.length) return null;
                  return (
                    <Box key={`${field.slot}-row-${rowIdx}`} className={styles.options_row}>
                      {rowOptions.map((opt) => (
                        <FormControlLabel
                          key={opt.value}
                          value={String(opt.value)}
                          control={<Radio checked={opt.value === currentValue} />}
                          label={opt.label}
                        />
                      ))}
                    </Box>
                  );
                })}
              </RadioGroup>
            </React.Fragment>
          );
        })}
      </FormControl>
      {errorMsg && errorComponent()}
      <Box className={styles.popover_ftr}>
        <Box className={styles.multi_button_container}>
          <Button to={"#"} className={styles.blue_btn} onClick={() => handleSave()}>
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
